import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import type { GenerationTelemetry, ReviewTrace } from '../../../types/siteRag';
import { DEFAULT_THEME, type ReportTheme } from '../../../types/reportTheme';
import { roleColor } from '../../../lib/reportRoleColors';
import { EditorialBadge } from './editorial';

/**
 * R-EDIT (env-charts) — editorial chrome conversion.
 *
 * The admin generation-cost telemetry panel previously rendered its
 * key-source / review-convergence pills and per-stage OK/✗ glyphs with
 * literal status classes (`bg-warning/10`, `text-success`,
 * `text-danger`) and a fixed app-token surface. The pills are now
 * theme-aware `EditorialBadge` tags and the success/fail glyphs read
 * `roleColor(theme, 'positive'|'negative')`.
 *
 * The host call site (SiteRagReportView) does not currently pass a
 * `ReportTheme` into this admin panel, so `theme` is OPTIONAL and falls
 * back to DEFAULT_THEME — the panel stays visually consistent for
 * legacy / theme-less callers (RULE: additive, no data loss) while
 * picking up the active preset whenever a theme is supplied.
 */

interface Props {
  telemetry?: GenerationTelemetry;
  reviewTrace?: ReviewTrace;
  isAdmin: boolean;
  /** Optional report theme; defaults to DEFAULT_THEME for theme-less callers. */
  theme?: ReportTheme;
}

function formatCost(usd: number): string {
  if (usd === 0) return '$0';
  if (usd < 0.01) return `$${usd.toFixed(4)}`;
  if (usd < 1) return `$${usd.toFixed(3)}`;
  return `$${usd.toFixed(2)}`;
}

function formatLatency(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  return `${Math.floor(ms / 60_000)}m ${Math.round((ms % 60_000) / 1000)}s`;
}

function formatTokens(n: number): string {
  if (n < 1000) return String(n);
  if (n < 1_000_000) return `${(n / 1000).toFixed(1)}K`;
  return `${(n / 1_000_000).toFixed(2)}M`;
}

export function GenerationCostPanel({ telemetry, reviewTrace, isAdmin, theme = DEFAULT_THEME }: Props) {
  const [collapsed, setCollapsed] = useState(true);
  if (!isAdmin) return null;

  if (!telemetry) {
    return (
      <div className="border border-border bg-card">
        <div className="px-4 py-3">
          <span className="text-sm font-bold uppercase tracking-widest text-text-secondary">Generation Cost</span>
          <div className="text-xs text-text-muted mt-1">Telemetry not captured (legacy report).</div>
        </div>
      </div>
    );
  }

  const { calls, totalPromptTokens, totalCompletionTokens, totalTokens, totalLatencyMs, estimatedCostUsd, modelMix, promptVersion, keySourceMix } = telemetry;

  return (
    <div className="border border-border bg-card">
      <button
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-muted transition-colors"
        onClick={() => setCollapsed(!collapsed)}
      >
        <div className="flex items-center gap-3">
          <span className="text-sm font-bold uppercase tracking-widest text-text-secondary">Generation Cost</span>
          {collapsed && (
            <>
              <span className="text-xs font-mono text-text-primary font-bold">{formatCost(estimatedCostUsd)}</span>
              <span className="text-xs font-mono text-text-muted">{formatTokens(totalTokens)} tokens</span>
              <span className="text-xs font-mono text-text-muted">{formatLatency(totalLatencyMs)}</span>
              <span className="text-xs font-mono text-text-muted">{calls.length} call(s)</span>
              <span className="text-xs font-mono text-text-muted" title="Prompt config version">{promptVersion}</span>
            </>
          )}
        </div>
        <ChevronDown className={`w-4 h-4 text-text-secondary transition-transform ${collapsed ? '' : 'rotate-180'}`} />
      </button>
      {!collapsed && (
        <div className="px-4 pb-4 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="p-3 border border-border bg-state-hover">
              <div className="text-xs font-bold uppercase tracking-widest text-text-muted">Estimated Cost</div>
              <div className="text-lg font-bold text-text-primary mt-1">{formatCost(estimatedCostUsd)}</div>
            </div>
            <div className="p-3 border border-border bg-state-hover">
              <div className="text-xs font-bold uppercase tracking-widest text-text-muted">Total Tokens</div>
              <div className="text-lg font-bold text-text-primary mt-1">{formatTokens(totalTokens)}</div>
              <div className="text-xs text-text-muted mt-1">
                {formatTokens(totalPromptTokens)} in / {formatTokens(totalCompletionTokens)} out
              </div>
            </div>
            <div className="p-3 border border-border bg-state-hover">
              <div className="text-xs font-bold uppercase tracking-widest text-text-muted">Wall Clock</div>
              <div className="text-lg font-bold text-text-primary mt-1">{formatLatency(totalLatencyMs)}</div>
              <div className="text-xs text-text-muted mt-1">{calls.length} call(s)</div>
            </div>
          </div>

          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-text-muted mb-2">Model Mix</div>
            <div className="flex flex-wrap gap-2">
              {Object.entries(modelMix).map(([model, n]) => (
                <span key={model} className="text-xs font-mono px-2 py-1 bg-state-hover border border-border">
                  {model} × {n}
                </span>
              ))}
            </div>
          </div>

          {keySourceMix && (keySourceMix.user > 0 || keySourceMix.org > 0) && (
            <div>
              <div className="text-xs font-bold uppercase tracking-widest text-text-muted mb-2">Key Source</div>
              <div className="flex flex-wrap gap-2">
                {keySourceMix.user > 0 && (
                  <EditorialBadge theme={theme} tone="warning" variant="outline" className="font-mono">
                    user × {keySourceMix.user}
                  </EditorialBadge>
                )}
                {keySourceMix.org > 0 && (
                  <span className="text-xs font-mono px-2 py-1 bg-state-hover border border-border">
                    org × {keySourceMix.org}
                  </span>
                )}
                {keySourceMix.unknown > 0 && (
                  <span className="text-xs font-mono px-2 py-1 bg-state-hover border border-border text-text-muted">
                    unknown × {keySourceMix.unknown}
                  </span>
                )}
              </div>
            </div>
          )}

          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-text-muted mb-2">Per-Stage Breakdown</div>
            <div className="border border-border overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-state-hover border-b border-border">
                    <th className="text-left px-2 py-1 font-bold uppercase tracking-widest text-text-muted">Stage</th>
                    <th className="text-left px-2 py-1 font-bold uppercase tracking-widest text-text-muted">Model</th>
                    <th className="text-right px-2 py-1 font-bold uppercase tracking-widest text-text-muted">In</th>
                    <th className="text-right px-2 py-1 font-bold uppercase tracking-widest text-text-muted">Out</th>
                    <th className="text-right px-2 py-1 font-bold uppercase tracking-widest text-text-muted">Latency</th>
                    <th className="text-center px-2 py-1 font-bold uppercase tracking-widest text-text-muted">Try</th>
                    <th className="text-center px-2 py-1 font-bold uppercase tracking-widest text-text-muted">OK</th>
                  </tr>
                </thead>
                <tbody>
                  {calls.map((c, i) => (
                    <tr key={i} className="border-b border-border last:border-b-0">
                      <td className="px-2 py-1 font-mono text-text-primary">{c.stage}</td>
                      <td className="px-2 py-1 font-mono text-text-secondary">{c.model}</td>
                      <td className="px-2 py-1 font-mono text-right text-text-secondary">{formatTokens(c.promptTokens)}</td>
                      <td className="px-2 py-1 font-mono text-right text-text-secondary">{formatTokens(c.completionTokens)}</td>
                      <td className="px-2 py-1 font-mono text-right text-text-secondary">{formatLatency(c.latencyMs)}</td>
                      <td className="px-2 py-1 font-mono text-center text-text-secondary">{c.attempt}</td>
                      <td className="px-2 py-1 text-center">
                        {c.success
                          ? <span style={{ color: roleColor(theme, 'positive') }}>✓</span>
                          : <span style={{ color: roleColor(theme, 'negative') }}>✗</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {reviewTrace && (
            <div>
              <div className="text-xs font-bold uppercase tracking-widest text-text-muted mb-2">Review Convergence</div>
              <div className="p-3 border border-border bg-state-hover">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-text-muted">
                    {reviewTrace.iterationsRun} of {reviewTrace.iterationsCappedAt} iteration(s) · final score{' '}
                    <span className="font-bold text-text-primary">{reviewTrace.finalScore}</span>
                  </span>
                  <EditorialBadge
                    theme={theme}
                    tone={
                      reviewTrace.iterationsRun === 0 && (reviewTrace.earlyExited || reviewTrace.finalScore === 0)
                        ? 'negative'
                        : reviewTrace.earlyExited
                          ? 'positive'
                          : 'warning'
                    }
                    variant="outline"
                  >
                    {reviewTrace.iterationsRun === 0 && (reviewTrace.earlyExited || reviewTrace.finalScore === 0)
                      ? 'System Review Failure'
                      : reviewTrace.earlyExited
                        ? 'Early Exit'
                        : 'Capped'}
                  </EditorialBadge>
                </div>
                <div className="text-xs font-mono text-text-secondary mt-2">
                  scores: [{reviewTrace.scoresPerIteration.join(', ')}]
                </div>
              </div>
            </div>
          )}

          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-text-muted mb-2">Prompt Version</div>
            <div className="p-3 border border-border bg-state-hover">
              <div className="text-sm font-mono font-bold text-text-primary">{promptVersion}</div>
              <div className="text-xs text-text-muted mt-1">
                What changed: see{' '}
                <span className="font-mono text-text-secondary" title="Repo file with one entry per version bump">
                  src/services/promptChangelog.md
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
